import {
  Box,
  CircularProgress,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { HubCard } from "~/components/hub-card";
import { Link } from "react-router-dom";
import { ScopeType, SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useTopActionsCardWithInitiatives } from "./hooks/useTopActionsCardWithInitiatives";
import { PolicyIcon } from "~/icons";
import { DataTable } from "~/components/data-table";
import TopActionsCardEffortCell from "~/components/FirewatchRow/TopActionsCardEffortCell";
import { TopActionsAssetsCountCell } from "~/components/FirewatchRow/TopActionsAssetsCountCell";
import PaginationContained from "~/components/PaginationContained/PaginationContained";
import { ClampWithTooltip } from "~/components/ClampWithTooltip/ClampWithTooltip";
import { Initiative, OrderDirection } from "~/operations";
import { formatDistanceToNowStrict } from "date-fns";
import { Flex } from "~/components/Flex";
import { TopActionsCardSkeleton } from "~/pages/space/home-dashboard/TopActionsCard/components/TopActionsCardSkeleton";
import TopActionsCardEmptyState from "~/pages/space/home-dashboard/TopActionsCard/components/TopActionsCardEmptyState";
import { InitiativesTableActionCell } from "./components/InitiativesTableActionCell";
import { getInitiativeDetailUrl } from "~/lib/urlUtils";

interface TopActionsCardWithInitiativesProps {
  scope: SpaceOrWorkspaceScope;
  spaceScope: SpaceScope;
  lastRefreshed?: string;
  isRefreshing?: boolean;
}

export function TopActionsCardWithInitiatives({
  scope,
  spaceScope,
  lastRefreshed,
  isRefreshing,
}: TopActionsCardWithInitiativesProps) {
  const {
    initiatives,
    totalFindings,
    loading,
    tableHeaders,
    sortField,
    sortDirection,
    handleSortChange,
    page,
    handlePageChange,
    totalPages,
  } = useTopActionsCardWithInitiatives(scope);

  const isSpaceDashboard = scope.type === ScopeType.Space;
  const hasNoFindings = initiatives.length === 0;
  const isEmpty = hasNoFindings && !loading;
  const shouldHideTagline = (hasNoFindings && !loading) || !isSpaceDashboard;

  const tagline = shouldHideTagline ? (
    <></>
  ) : (
    <Flex
      sx={{
        color: "text.secondary",
        alignItems: "center",
        gap: 0.5,
        mt: 0.8,
        textTransform: "initial",
      }}
    >
      {isRefreshing && (
        <CircularProgress size={13} sx={{ color: "text.secondary" }} />
      )}

      <Typography variant="caption">
        <strong>{isRefreshing ? "Updating ..." : "Last Updated:"}</strong>{" "}
        {!isRefreshing && (
          <>
            {lastRefreshed
              ? `${formatDistanceToNowStrict(lastRefreshed)} ago`
              : "Never"}
          </>
        )}
      </Typography>
    </Flex>
  );

  const tooltip = (
    <Typography variant="caption">
      These are your highest-impact fixes, ranked by severity and how many
      assets are affected. We only include issues with a clear path to
      remediation
    </Typography>
  );

  const title = (
    <Flex justifyContent="space-between" alignItems="flex-start">
      <Typography
        variant="h5"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 700,
          mb: isSpaceDashboard ? 0 : "26px",
          fontSize: { xs: 20, md: 24 },
        }}
      >
        TOP ACTIONS FOR RISK REDUCTION
        <Tooltip title={tooltip} arrow placement="right">
          <InfoOutlinedIcon sx={{ color: "secondary.main", width: 16 }} />
        </Tooltip>
      </Typography>
    </Flex>
  );

  return (
    <HubCard
      loading={false}
      title={title}
      tagline={tagline}
      icon={<PolicyIcon />}
      data-testid="top-actions-card"
      sx={{
        minHeight: 376,
      }}
    >
      {!loading && !isEmpty && (
        <Stack
          justifyContent="space-between"
          flexDirection="column"
          height="100%"
        >
          <DataTable firewatchSize="small">
            <TableHead
              sx={{
                backgroundColor: "inherit",
                "& th": {
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  borderBottom: "none",
                  pl: 0,
                  pb: "0 !important",
                },
                ".MuiTableCell-root:first-of-type": {
                  pl: "5px",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <TableRow>
                {tableHeaders.map((header) => {
                  const { label, options, id } = header;

                  return (
                    <TableCell
                      key={id}
                      align={options?.textAlign}
                      width={options?.width}
                      sx={{
                        fontWeight: "500 !important",
                        textWrap: "nowrap",
                        ...options?.sx,
                      }}
                    >
                      {header.sortField ? (
                        <TableSortLabel
                          active={sortField === header.sortField}
                          sx={{
                            "& .MuiTableSortLabel-icon": { fontSize: 10 },
                          }}
                          onClick={() =>
                            handleSortChange(
                              header.sortField!,
                              sortField === header.sortField
                                ? sortDirection === OrderDirection.Asc
                                  ? OrderDirection.Desc
                                  : OrderDirection.Asc
                                : sortDirection,
                            )
                          }
                          direction={
                            sortDirection === OrderDirection.Asc
                              ? "asc"
                              : "desc"
                          }
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {initiatives.map((initiative: Initiative) => {
                const url = getInitiativeDetailUrl({
                  initiativeMrn: initiative.mrn,
                  scopeParams: scope.params,
                });

                const aggregateMetadata =
                  initiative.metadata?.__typename ===
                  "AggregateInitiativeMetadata"
                    ? initiative.metadata
                    : undefined;

                return (
                  <TableRow
                    key={initiative.mrn}
                    sx={{
                      ".MuiTableCell-root:first-of-type": {
                        pl: "5px",
                      },
                    }}
                    data-testid="top-action-risk-table-row"
                  >
                    <TableCell>
                      <Box
                        component={Link}
                        to={url}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ClampWithTooltip>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 400,
                              lineHeight: "16px",
                              color: "text.primary",
                            }}
                          >
                            {initiative.title}
                          </Typography>
                        </ClampWithTooltip>
                      </Box>
                    </TableCell>

                    <TopActionsAssetsCountCell
                      isSmallTable={false}
                      count={aggregateMetadata?.assetCount || 0}
                    />

                    <TableCell>
                      <Typography variant="caption">
                        {initiative.scoreDelta?.toFixed(2) ?? ""}
                      </Typography>
                    </TableCell>

                    <TopActionsCardEffortCell
                      isInitiative
                      manualEffort={aggregateMetadata?.manualEffort}
                      effort={aggregateMetadata?.effort || 0}
                    />

                    <InitiativesTableActionCell initiative={initiative} />
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>

          <PaginationContained
            itemCount={totalFindings}
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            itemType="finding"
          />
        </Stack>
      )}

      {loading && <TopActionsCardSkeleton />}

      {isEmpty && (
        <TopActionsCardEmptyState
          space={spaceScope}
          totalFindings={totalFindings}
          isSpaceDashboard={isSpaceDashboard}
        />
      )}
    </HubCard>
  );
}
